import React, { useEffect, useState, useRef, FC } from 'react';
import ReactDOM from 'react-dom';
import ReactPlayerLoader from '@brightcove/react-player-loader';

import { VideoProps } from './Video.types';
import { Modal, Image } from '@marriott/mi-ui-library';
import { canUseDOM } from '../../utils/helper';
import { StyledVideoPlayer, StyledVideoModal } from './Video.styles';
import clsx from 'clsx';

interface PlayerInstance {
  paused: () => boolean;
  pause: () => void;
  currentTime: () => number;
  setCurrentTime: (time: number) => void;
}

export const Video: FC<VideoProps> = props => {
  const {
    id,
    playerId = 'default',
    accountId,
    videoId,
    poster,
    clickTrack,
    custom_click_track_value,
    ariaLabel,
    renditions,
    enableMute = true,
    enableVideoLoop = false,
    onError,
  } = props;

  const [error, setError] = useState(false);
  const [viewPort, setViewPort] = useState('Desktop');
  const [player, setPlayer] = useState<PlayerInstance | null>(null);
  const [videoProgress, setVideoProgress] = useState(0);
  const [showModal, setShowModal] = useState<boolean>(false);
  const isMobileView = viewPort === 'MobileTablet';
  const modalRoot = canUseDOM && document.getElementsByTagName('body')[0];
  const playBtnRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    const screenWidth = window.innerWidth;
    if (screenWidth < 1200) {
      setViewPort('MobileTablet');
    }
  }, []);

  useEffect(() => {
    if (showModal) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'auto';
    }
  }, [showModal]);

  const handleResize = () => {
    const xs = window.matchMedia('(max-width: 1199px)');
    if (xs?.matches) {
      setViewPort('MobileTablet');
    } else {
      setViewPort('Desktop');
    }
  };

  useEffect(() => {
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [player]);

  useEffect(() => {
    if (error && onError) {
      onError();
    }
  }, [error, onError]);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        // If a Brightcove dialog (caption settings) is open, let Video.js
        // close it first — don't close the whole modal.
        const vjsDialog = document.querySelector('.vjs-modal-dialog:not(.vjs-hidden)');
        if (vjsDialog) return;
        closeModal();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  useEffect(() => {
    const handleFullScreenChange = () => {
      if (!document?.pictureInPictureElement && !document?.fullscreenElement) {
        closeModal();
      }
    };

    document.addEventListener('fullscreenchange', handleFullScreenChange);
    return () => {
      document.removeEventListener('fullscreenchange', handleFullScreenChange);
    };
  }, []);

  useEffect(() => {
    if (!showModal) return;

    // Selector for all keyboard-focusable elements, excluding tabindex="-1" (programmatic-only focus).
    // Same selector pattern used across the monorepo (e.g. Modal focus trap, createFocusTrap utility).
    const FOCUSABLE_SELECTOR =
      'button:not([disabled]), a[href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

    // Auto-focus the close button once the modal and Brightcove player have rendered.
    // Uses a MutationObserver instead of a fixed timeout so focus lands reliably
    // regardless of player load speed (same pattern as PhotoEssay).
    const observer = new MutationObserver(() => {
      const closeBtn = document.querySelector<HTMLElement>('.video-modal-main-container .popup-close');
      if (closeBtn) {
        observer.disconnect();
        closeBtn.focus();
      }
    });
    observer.observe(document.body, { childList: true, subtree: true });
    // Fallback: if the close button is already in the DOM (fast render), focus immediately.
    const existingCloseBtn = document.querySelector<HTMLElement>('.video-modal-main-container .popup-close');
    if (existingCloseBtn) {
      observer.disconnect();
      existingCloseBtn.focus();
    }

    // Focus trap: only intercept Tab at the modal boundaries to prevent escape.
    // All other Tab navigation (including inside Brightcove dialogs, dropdowns, etc.)
    // is handled natively by the browser — no interference.
    const handleTabTrap = (event: KeyboardEvent) => {
      if (event.key !== 'Tab') return;

      const modalEl = document.querySelector<HTMLElement>('.video-modal-main-container');
      if (!modalEl) return;

      const isInsideModal = modalEl.contains(document.activeElement);

      // Focus is outside the modal while it's open — bring it back
      if (!isInsideModal) {
        event.preventDefault();
        const closeBtn = modalEl.querySelector<HTMLElement>('.popup-close');
        if (closeBtn) {
          closeBtn.focus();
        }
        return;
      }

      // Only wrap at boundaries. Use querySelectorAll fresh each time since
      // Brightcove dialogs (caption settings, quality menu) add/remove elements dynamically.
      // When a VJS dialog (e.g. caption settings) is open, skip control bar buttons
      // behind it — they are invisible tab stops that confuse keyboard navigation.
      const vjsDialogOpen = !!modalEl.querySelector('.vjs-modal-dialog:not(.vjs-hidden)');
      const focusables = Array.from(modalEl.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)).filter(
        el =>
          !el.classList.contains('video-modal-container') &&
          !el.classList.contains('video-modal-container-mobile') &&
          !el.closest('.vjs-hidden') &&
          !(vjsDialogOpen && el.closest('.vjs-control-bar'))
      );
      if (focusables.length < 2) return;

      const first = focusables[0];
      const last = focusables[focusables.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last?.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first?.focus();
      }
    };

    document.addEventListener('keydown', handleTabTrap);

    return () => {
      observer.disconnect();
      document.removeEventListener('keydown', handleTabTrap);
    };
  }, [showModal]);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleSuccess = (success: any) => {
    const { ref } = success;
    if (!ref) return;

    ref.on('error', () => {
      setError(true);
    });
    const directionValue = document?.documentElement?.getAttribute('dir');
    const videoControllers = ref?.el_?.querySelector('.vjs-control-bar');
    if (directionValue === 'rtl' && videoControllers) {
      videoControllers.setAttribute('dir', 'rtl');
    }

    if (clickTrack) {
      const playControlButton = ref?.el_?.querySelector('.vjs-play-control');
      if (playControlButton) {
        playControlButton.setAttribute('data-custom_click_track_value', custom_click_track_value);
        playControlButton.classList.add('custom_click_track');
      }
    }

    // Set the player instance when it's ready
    setPlayer(ref);

    // Set the video progress when the player is ready
    ref.on('ready', () => {
      ref.currentTime(videoProgress);
    });

    // Save the video progress when the video ends
    ref.on('ended', () => {
      setVideoProgress(ref.currentTime());
    });

    // Save the video progress when the video is paused
    ref.on('pause', () => {
      setVideoProgress(ref.currentTime());
    });

    // Update the video progress while the video is playing
    ref.on('timeupdate', () => {
      setVideoProgress(ref.currentTime());
    });
  };

  const closeModal = () => {
    setShowModal(false);
    // Restore focus after React unmounts the modal portal,
    // so Tab continues to the next page element instead of jumping to the top.
    requestAnimationFrame(() => {
      playBtnRef.current?.focus();
    });
  };

  const renderPlayer = (autoplay: boolean) => (
    <ReactPlayerLoader
      id={id}
      accountId={accountId}
      videoId={videoId}
      playerId={playerId}
      poster={poster}
      onSuccess={handleSuccess}
      onFailure={() => setError(true)}
      options={{
        responsive: true,
        autoplay,
        preload: 'none',
        muted: enableMute,
        aspectRatio: '16:9',
        loop: enableVideoLoop,
        src: 'https://players.brightcove.net',
        userActions: {
          doubleClick: false,
        },
      }}
      aria-label={ariaLabel}
    />
  );

  return (
    <StyledVideoPlayer>
      <div className="video-image-container">
        <Image altText={''} renditions={renditions} dynamic={false} defaultImageURL={poster} />
        <div className="video-image-btn-container">
          <button
            className="video-image-play-btn"
            aria-label="Play"
            custom_click_track_value=""
            ref={playBtnRef}
            onClick={() => {
              setShowModal(true);
            }}
          >
            <svg fill="none" height="90" viewBox="0 0 90 90" width="90" xmlns="http://www.w3.org/2000/svg">
              <circle cx="45" cy="45" fill="#1C1C1C" fillOpacity="0.62" r="45"></circle>
              <path d="M63 45.5L36.75 60.6554L36.75 30.3446L63 45.5Z" fill="#EEEEEE"></path>
            </svg>
          </button>
        </div>
      </div>
      {showModal &&
        modalRoot &&
        ReactDOM.createPortal(
          <StyledVideoModal>
            <Modal
              show={showModal}
              className="video-modal-main-container"
              secondaryClassName={clsx(isMobileView ? 'video-modal-container-mobile' : 'video-modal-container')}
            >
              <Modal.Header
                labelText={''}
                popupHeaderOnCLoseFunc={closeModal}
                className="video-modal-header"
                popupCloseClassName="video-close-btn m-icon-button-primary"
              />
              <Modal.Body className="p-0 video-modal-body">{renderPlayer(true)}</Modal.Body>
            </Modal>
          </StyledVideoModal>,
          modalRoot
        )}
    </StyledVideoPlayer>
  );
};
